import { useState, type FormEvent } from "react";
import { toast } from "react-hot-toast";

import {
  useUpdateWorkspaceProfile,
  useWorkspaceSession,
  type WorkspaceIdentity,
} from "../api/workspaceSession";
import { Button, FieldRow, TextInput } from "../components/ui";
import {
  Empty,
  ErrorState,
  Loading,
  PageHeader,
  Panel,
} from "../components/WorkspacePrimitives";
import SettingsLayout from "./SettingsLayout";

function NameSettings({ user }: { user: WorkspaceIdentity }) {
  const update = useUpdateWorkspaceProfile();
  const [firstName, setFirstName] = useState(user.first_name ?? "");
  const [familyName, setFamilyName] = useState(user.family_name ?? "");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await update.mutateAsync({
        first_name: firstName.trim() || null,
        family_name: familyName.trim() || null,
      });
      toast.success("Name updated.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Update failed.");
    }
  };

  return (
    <form className="profile-settings-form" onSubmit={submit}>
      <div className="profile-settings-form__names">
        <FieldRow className="profile-settings-form__field" label="First name">
          <TextInput
            aria-label="First name"
            value={firstName}
            onValueChange={setFirstName}
            maxLength={100}
            autoComplete="given-name"
          />
        </FieldRow>
        <FieldRow className="profile-settings-form__field" label="Last name">
          <TextInput
            aria-label="Last name"
            value={familyName}
            onValueChange={setFamilyName}
            maxLength={100}
            autoComplete="family-name"
          />
        </FieldRow>
        <div className="profile-settings-form__actions">
          <Button tone="primary" type="submit" disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save name"}
          </Button>
        </div>
      </div>
    </form>
  );
}

export default function ProfileSettingsPage() {
  const session = useWorkspaceSession();
  const currentUser = session.data?.identity;

  return (
    <SettingsLayout>
      <div className="page page--workspace token-settings-page">
        <PageHeader className="settings-page-header">
          <h1>Profile</h1>
        </PageHeader>
        <Panel title="Name">
          {session.isPending && !session.data ? (
            <Loading label="Loading profile…" />
          ) : session.error && !session.data ? (
            <ErrorState
              error={session.error}
              onRetry={() => session.refetch()}
            />
          ) : currentUser ? (
            <NameSettings key={currentUser.subject} user={currentUser} />
          ) : (
            <Empty>Your workspace profile is unavailable.</Empty>
          )}
        </Panel>
      </div>
    </SettingsLayout>
  );
}
